import { useEffect, useState } from 'react'
import { handleError } from '../../../lib/utils'
import { message } from 'antd'
import {
  alreadyEnrolled,
  cancelEnrollement,
  enrollPatient,
  fetchEnrollment,
  fetchEnrollmentAggregate
} from '../../Chronic Care Plans/utils/utils'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { readSystemUsersAggregate } from '../../../Admin/system-users/utils/utils'
import { debounce } from 'lodash'

// This hook combines functionality from both useCarePlanEnrollment and useEnrollmentSearch
const usePatientEnrollment = ({ template, planType }) => {
  const authState = useSelector((state) => state.auth)

  // Enrollment list related state (from useCarePlanEnrollment)
  const [enrolledUsers, setEnrolledUsers] = useState([])
  const [newEnrollment, setNewEnrollment] = useState(false)
  const [loadingPatients, setLoadingPatients] = useState(false)
  const [currentPatient, setCurrentPatient] = useState(null)

  // Enrollment search related state (from useEnrollmentSearch)
  const [searchQuery, setSearchQuery] = useState('')
  const [patient, setPatient] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [enrolling, setEnrolling] = useState(false)
  const [teamMembers, setTeamMembers] = useState([])
  const [searching, setSearching] = useState(false)
  const [enrollmentOverride, setEnrollmentOverride] = useState(dayjs())

  // Load enrolled users on component mount
  useEffect(() => {
    handleFetchEnrolledUsers()
    // eslint-disable-next-line
  }, [])

  // Listen for search query changes
  useEffect(() => {
    handlePatientSearch()
    // eslint-disable-next-line
  }, [searchQuery])

  // From useCarePlanEnrollment
  const handleFetchEnrolledUsers = async () => {
    let tmpUsers = []
    setLoadingPatients(true)
    try {
      const tmpUsersAggr = await fetchEnrollmentAggregate({
        templateRef: template._id,
        planType: planType || 'care',
        $or: [
          { teamMembers: authState.agiliteUser._id }, // Direct match if teamMembers contains ObjectIds
          { 'audit.createdBy': authState.agiliteUser._id },
          { doctorRef: authState.agiliteUser._id }
        ]
      })

      tmpUsers = await fetchEnrollment({
        templateRef: template._id,
        planType: planType || 'care',
        $or: [
          { teamMembers: authState.agiliteUser._id }, // Direct match if teamMembers contains ObjectIds
          { 'audit.createdBy': authState.agiliteUser._id },
          { doctorRef: authState.agiliteUser._id }
        ]
      })
      setEnrolledUsers(tmpUsers)
    } catch (error) {
      message.error(handleError(error))
    }
    setLoadingPatients(false)
  }

  const handleNewEnrollment = (record) => {
    let tmpEnrolledUsers = [...enrolledUsers, record]
    setEnrolledUsers(tmpEnrolledUsers)
  }

  const handleCancelEnrollment = async (record) => {
    let tmpEnrollment = [...enrolledUsers]
    let entryIndex = tmpEnrollment.findIndex((i) => i._id === record._id)
    try {
      await cancelEnrollement(record._id)
      tmpEnrollment.splice(entryIndex, 1)
      setEnrolledUsers(tmpEnrollment)
    } catch (error) {
      message.error(handleError(error))
    }
  }

  // From useEnrollmentSearch
  const handleReset = () => {
    setPatient(null)
    setSearchQuery('')
    setSearchResults([])
    setNewEnrollment(false)
  }

  const handleEnrollPatient = async () => {
    let tmpResponse = null

    setEnrolling(true)
    try {
      const enrolled = await alreadyEnrolled(patient._id, template._id, planType)
      if (enrolled) {
        message.error('Patient already enrolled.')
        setEnrolling(false)
        return
      }
      tmpResponse = await enrollPatient(patient, teamMembers, template, enrollmentOverride, planType)
      handleNewEnrollment({ ...tmpResponse, patientProfile: patient })
      handleReset()
    } catch (error) {
      message.error(handleError(error))
    }
    setEnrolling(false)
  }

  const handlePatientSearch = async () => {
    try {
      let tmpUsers = []
      let qry = { 'extraData.role.type': 'patient' }
      setSearching(true)
      try {
        if (searchQuery) {
          const searchTerms = searchQuery.trim().toLowerCase().split(/\s+/)

          // Prepare regex patterns for partial matching
          const regexPatterns = searchTerms.map((term) => new RegExp(term, 'i'))
          qry = {
            ...qry,
            $or: [
              // Match against concatenated firstName and lastName with partial regexes
              {
                $expr: {
                  $regexMatch: {
                    input: {
                      $concat: [{ $toLower: '$firstName' }, ' ', { $toLower: '$lastName' }]
                    },
                    regex: searchTerms.map((term) => `.*${term}.*`).join(''),
                    options: 'i'
                  }
                }
              },
              // Match individually against firstName and lastName with partial regexes
              {
                $or: [
                  {
                    $expr: {
                      $regexMatch: {
                        input: { $toLower: '$firstName' },
                        regex: regexPatterns.join('|'),
                        options: 'i'
                      }
                    }
                  },
                  {
                    $expr: {
                      $regexMatch: {
                        input: { $toLower: '$lastName' },
                        regex: regexPatterns.join('|'),
                        options: 'i'
                      }
                    }
                  }
                ]
              },
              { phoneNumber: { $regex: searchQuery, $options: 'i' } },
              { email: { $regex: searchQuery, $options: 'i' } },
              { physicalAddress: { $regex: searchQuery, $options: 'i' } },
              { idNo: { $regex: searchQuery, $options: 'i' } }
            ]
          }
        }
        tmpUsers = await readSystemUsersAggregate(qry)
        setSearchResults(tmpUsers)
      } catch (error) {
        message.error(handleError(error))
      }
    } catch (error) {
      message.error(handleError(error))
    }
    setSearching(false)
  }

  const handlePatientQuery = debounce((query) => {
    setSearchQuery(query)
  }, 300)

  const handleTeamMemberSelection = (newTeamMember) => {
    let tmpTeamMembers = [...teamMembers]
    const findExistingTeamMember = tmpTeamMembers.findIndex((member) => member === newTeamMember)
    if (findExistingTeamMember > -1) {
      tmpTeamMembers.splice(findExistingTeamMember, 1)
    } else {
      tmpTeamMembers.push(newTeamMember)
    }
    setTeamMembers(tmpTeamMembers)
  }

  return {
    // From useCarePlanEnrollment
    enrolledUsers,
    handleFetchEnrolledUsers,
    handleNewEnrollment,
    handleCancelEnrollment,
    loadingPatients,
    currentPatient,
    setCurrentPatient,
    newEnrollment,
    setNewEnrollment,

    // From useEnrollmentSearch
    searchQuery,
    setSearchQuery,
    patient,
    setPatient,
    searchResults,
    handleEnrollPatient,
    searching,
    enrolling,
    enrollmentOverride,
    setEnrollmentOverride,
    handleTeamMemberSelection,
    teamMembers,
    handlePatientQuery,
    handleReset
  }
}

export default usePatientEnrollment
